import moment from 'moment';
import { parseDateRange, RANGE } from '../../src/ui/constants/marketdata';
import { createETMoment } from '../../src/ui/util/marketdata'; 
import { MONTHS } from '../../src/chart/data/constants';
const fetch = require('node-fetch');
const { URL, URLSearchParams } = require('url');
const hstri = require('hstri');

// functions to export
async function getTickerSymbolIndex(url) {
  return await fetch(url); 
}

function parseURL(req, type) {
  const fullUrl = req.protocol + '://' + req.get('host') + req.originalUrl;
  const requestURL = new URL(fullUrl);
  const getSearchParams = createParamsSearch(requestURL);

  return type === 'ticker' ?
    getSearchParams('stockIndex')
    :
    getMikeParams(getSearchParams);
}

function createDataFormatter(res, isPercent, isCustom, dateRange, frequency, end, instruments) {
  return function(data) {
    const series = formatForChartlos(data, isPercent, isCustom, dateRange, frequency, end, instruments);
    res.send({ series });
  }
}

function fetchData(instrument, start, end, frequency, isCustom, dateRange, isRolling, product, done) {
  const isIntraday = /\d+D/.test(dateRange);
  const formattedEnd = findEndDate(end, isRolling, isCustom, isIntraday);

  const token = getToken(product);
  
  queryHstri(start, instrument, frequency, formattedEnd, token, dateRange)
    .then(data => done(data));
}

function createDataParser(res, isPercent, isCustom, frequency, dateRange, end) {
  const isMultiple = res.length > 1;

  const showAsPercentProps = checkShowAsPercent(res, isPercent);
  const usePercent = isMultiple &&
    (isPercent || mustShowAsPercent(showAsPercentProps));

  const { TimeInfo, Series } = res[0].raw;
  const { IsIntraday } = TimeInfo;
  
  const startDates = findStartDates(Series, dateRange);

  const needsLast = !isCustom || checkToday(isCustom, IsIntraday, end, moment());
  const allDates = buildDatesArr(res, [...startDates], end, isCustom, frequency, needsLast);

  const lastIndex = getLastIdx(allDates); 

  const categories = createCategories(allDates, needsLast, lastIndex, IsIntraday, frequency);

  const date = formatAsOfDate(allDates[lastIndex], IsIntraday, frequency);

  const dataTemplate = buildDataTemplate(allDates);

  return function(series, index) {
    const { data, raw } = series;
    const { InstrumentType, ExtraData } = raw.Series[index];
    
    const { Value } = findPriorClose(ExtraData);
    const priorDate = startDates[index];
    
    const seriesArr = mapSeriesData(data, usePercent, InstrumentType, priorDate, Value, dataTemplate);
    
    return [seriesArr, categories, date, showAsPercentProps];
  }
}

// parseURL helper functions
function createParamsSearch(requestURL) {
  return function (prop, getAll) {
    const action = getAll ? 'getAll' : 'get';
    return requestURL.searchParams[action](prop);
  }
}

function getMikeParams(getSearchParams) {
  return ['instrument', 'start', 'end', 'frequency', 'format', 'dateRange', 'product', 'type']
    .map(prop => getParam(getSearchParams, prop));
}

function getParam(getSearchParams, prop) {
  const param = getSearchParams(prop, prop === 'instrument');
  switch(prop) {
    case 'format':
      return param === 'percent';
    case 'type':
      return param === 'rolling';
    default:
      return param;
  }
}

// createDataFormatter helper functions
function formatForChartlos(res, isPercent, isCustom, dateRange, frequency, end, instruments) {
  const parseData = createDataParser(res, isPercent, isCustom, frequency, dateRange, end);  
  return res.map((series, index) => {
    const [seriesArr, categories, date, showAsPercentProps] = parseData(series, index);
    const { raw, name, ticker } = series;
    const { TimeInfo, Series } = raw;
    const { IsIntraday: isIntraday } = TimeInfo;
    const {
      CountryCode: country,
      InstrumentType: tickerType,
      FormatHints
    } = Series[index];
    const instrument = instruments[index];
    const sourceInfo = getSourceInfo(tickerType, ticker, instrument);
    const [prefix, suffix, decimals] = setFormatProps(FormatHints);
    const needsCommas = tickerType !== 'Index';    
    return ({
      data: seriesArr,
      name,
      symbol: ticker,
      country,
      needsCommas,
      index,
      date,
      prefix,
      suffix,
      decimals,
      isIntraday,
      sourceInfo,
      tickerType,
      showAsPercentProps,
      categories
    })
  })
}

function checkShowAsPercent(series, isPercentProp) {
  // "Show as percent change" toggle should be locked into true or false in certain situations
  const hasBonds = series.some(checkBonds);
  const differentUnitSymbols = series.some((currentSeries, i) =>
    checkUnitSymbols(series[0], currentSeries, i));
  const disabled = hasBonds || differentUnitSymbols// || series.length === 1;
  const isPercent = !hasBonds && (differentUnitSymbols || isPercentProp);
  return { isPercent, disabled };
}

function checkBonds(series, i) {
  const instrumentType = getSeriesProp(series, i, 'InstrumentType');
  return instrumentType === 'Bond';
}

function getSeriesProp(series, i, prop) {
  return series.raw.Series[i][prop];
}

function checkUnitSymbols(firstSeries, currentSeries, i) {
  return getUnitSymbol(currentSeries, i) !== getUnitSymbol(firstSeries, 0);
}

function getUnitSymbol(series, i) {
  return getSeriesProp(series, i, 'FormatHints').UnitSymbol;
}

function mustShowAsPercent(showAsPercentProps) {
  const { isPercent, disabled } = showAsPercentProps;
  return isPercent && disabled;
}

function mapSeriesData(data, isMultiple, usePercent, tickerType, startTime, isIntraday) {
  const seriesData = [],
    categories = [];

  const initialY = findFirstY(data);

  data.forEach((item, index) => {
    const x = item[0];
    
    const preStart = isMultiple && x < startTime;
    const y = getY(item[1], initialY, usePercent, tickerType, preStart);
    
    const category = formatCategory(x, isIntraday);
    
    seriesData.push({ index, x, y });
    categories.push(category);
  });

  return [seriesData, categories];
}

function findFirstY(data) {
  // find first non-null Y value (needed to calculate percent change)
  return data.find(point => point[1])[1];
}

function getY(y, initialY, usePercent, tickerType, preStart) {
  // for tickers from different time zones, don't plot ticker's Y values before its time frame starts
  if (preStart || y === null) return null;

  if (!usePercent || tickerType === 'Bond') return y;

  return getChange(initialY, y);
}

function getChange(initialY, y) {
  // percentage change from first Y value
  return (y - initialY) / initialY * 100;
}

function formatCategory(x, isIntraday) {
  const date = createETMoment(x);
  const dayFormat = 'MM/DD/YYYY';
  return isIntraday ?
    date.format(`${dayFormat} h:mm:ss a`).slice(0, -1)
    :
    date.utc().format(dayFormat);
}

function getSourceInfo(type, symbol, fullSymbol) {
  return {
    source: getSource(type, symbol, fullSymbol),
    type: getPlural(type),
    symbol
  }
}

function getSource(type, symbol, fullSymbol) {
  switch(type) {
    case 'Bond':
    case 'Currency':
      return 'Tullett Prebon Information';
    case 'Index':
      return getIndexSource(symbol);
    case 'CryptoCurrency':
      return getCryptoSource(symbol, fullSymbol);
    default:
      return 'SIX';
  }
}

function getIndexSource(symbol) {
  switch(symbol) {
    case 'BUXX':
    case 'NEXT50':
    case 'VCIPO':
      return 'Dow Jones Market Data';
    default:
      return 'SIX';
  }
}

function getCryptoSource(symbol, fullSymbol) {
  const symbols = ['BTCEUR', 'BTCUSD', 'ETHEUR', 'ETHUSD', 'XRPEUR', 'XRPUSD', 'LTCEUR', 'LTCUSD', 'BCHEUR', 'BCHUSD', 'XMREUR', 'XMRUSD', 'ETCEUR', 'ETCUSD', 'DASHEUR', 'DASHUSD', 'ZECEUR', 'ZECUSD'];
  return symbols.includes(symbol) && fullSymbol.includes('KRAKEN') ?
    'KRAKEN'
    :
    'CoinDesk';
}

function getPlural(type) {
  switch(type){
    case 'Index':
      return 'indexes';
    case 'Commodity':
      return 'commodities';
    case 'CryptoCurrency':
      return 'crypto currencies';
    case 'Currency':
      return 'currencies';
    case 'ExchangeTradedFund':
      return 'ETFs';
    case 'ExchangeTradedNote':
      return 'ETNs';
    case 'Adr':
      return 'ADRs';
    case 'PreferredStock':
      return 'preferred stocks';
    // case 'MutualFundClosed':
    //   return;
    // case 'MutualFundOpen':
    //   return;
    // case 'RealEstateInvestmentTrust':
    //   return;
    default:
      return `${type.toLowerCase()}s`;
  }
}

function setFormatProps(formatHints) {
  const { UnitSymbol, DecimalPlaces, Suffix } = formatHints;
  const prefix = Suffix || !UnitSymbol ?
    '' : UnitSymbol;
  const suffix = Suffix ?
    UnitSymbol : '';
  const decimals = UnitSymbol === '%' || DecimalPlaces <= 2 ?
    DecimalPlaces : 2;
  return [prefix, suffix, decimals];
}

// function findStartDate(start) {
//   return start[0] === 'Y' ?
//     findYTDStart()
//     :
//     subtractDate(start);
// }

// function findYTDStart() {
//   // check forward from 12/26 to find date of previous year's last trading session
//   return moment().subtract(1, 'year').month(11).date(26);
// }

// function subtractDate(start) {
//   const { timePeriodType, timeValue } = parseDateRange(start);
//   const [timeType, multiplier] = timePeriodType === 'Y' ?
//     ['weeks', 52] // 1Y = 52 weeks, etc.
//     :
//     [getTimeFrame(timePeriodType), 1];
//   return moment().subtract(timeValue * multiplier, timeType);
// }

// function getTimeFrame(code) {
//   switch (code) {
//     case 'D':
//       return 'days';
//     case 'Y':
//       return 'years';
//     case 'M':
//       return 'months';
//   }
// }

// fetchData helper functions
function findEndDate(end, isRolling, isCustom, isIntraday) {
  const currentTime = moment();
  /*
   * For custom auto-updating charts, update only until specified end date
  */
  if (isRolling && isCustom) return moment.min(moment(end), currentTime);
  
  /*
   * Use the current time for custom with an end date of today
  */
  const isCustomToday = checkToday(isCustom, isIntraday, end, currentTime);
  if (isCustomToday) return currentTime;
  
  /* 
   * Otherwise use specified end date
  */
  return end;
}

function checkToday(isCustom, isIntraday, end, currentTime) {
  return isCustom && !isIntraday
    && end.slice(0, 10) === currentTime.format('YYYY-MM-DD');
}

function getToken(product) {
  switch (product) {
    case 'wsj':
      return '57494d5ed7ad44af85bc59a51dd87c90';
    case 'barrons':
      return 'c1b7ca756343461b86565b61bbd2ac6f';
    case 'marketwatch':
      return 'cecc4267a0194af89ca343805a3e57af';
    case 'mansionglobal':
      return '45mb4gzz6uqrogv7xgv37k654z2e5d6q';
    case 'financialnews':
      return 'brewtbcvblmff8sb1kcdbj21c29j8lv5';
    default:
      return '57494d5ed7ad44af85bc59a51dd87c90';
  }
// WSJ Pro: 'eb0af67ce7f24679a0e2d2c7c32e7d98'
}

function queryHstri(start, instrument, frequency, end, entitlementToken, timeFrame) {
  return hstri({
    start: getHstriDate(start),
    timeFrame: RANGE[timeFrame].value,
    instrument,
    frequency: frequency || 'P1D',
    end: getHstriDate(end),
    service: 'michelangelo',
    entitlementToken
  });
}

function getHstriDate(date) {
  return date ? createETMoment(date) : '';
}

// function getLastDate(res) {
//   const { data } = res[0];
//   return data[data.length - 1][0];
// }

// function findStartTimes(data, timeFrame) {
//   const startTimes = {};
//   data.forEach((series, index) => {
//     const { ticker, raw } = series;
//     const { MarketSessions, InstrumentType } = raw.Series[index];
//     const sessions = MarketSessions.reverse();
//     startTimes[ticker] = findStartTime(sessions, InstrumentType, timeFrame);
//   })
//   return startTimes;
// }

// function findStartTime(sessions, instrumentType, timeFrame) {
//   // remove all holidays before counting back correct number of days
//   const holidaysRemoved = sessions.filter(session => session.Kind !== 'Holiday');
//   return filterSessions(holidaysRemoved, instrumentType, timeFrame);
// }

// function filterSessions(holidaysRemoved, instrumentType, timeFrame) {
//   const filter = checkClosed(holidaysRemoved) || instrumentType === 'Future' ?
//     countBy24Hours : countClosedSessions;
//   return filter(holidaysRemoved, timeFrame);
// }

// function checkClosed(sessions) {
//   // check whether instrument has overnight closed session
//   for (let i = 0; i < sessions.length; i++) {
//     const { Kind, End, Start } = sessions[i];    
//     if (Kind === 'Closed') {
//       const closedLength = End - Start;
//       const minute = 1000 * 60;
//       const closed1Min = closedLength === minute;
//       if (closed1Min) return true;
//     }
//   }
//   return false;
// }

// function countBy24Hours(sessions, timeFrame) {
//   // for instruments without overnight closed sessions
//   const tradingSessions = sessions.filter(session => session.Kind === 'Trading');
//   const { End } = tradingSessions[0];
//   const currentTimestamp = moment().valueOf();
//   const tradingActive = currentTimestamp <= End;
//   if (!tradingActive) timeFrame -= 1;
//   const startingSession = tradingSessions[timeFrame];
//   return tradingActive ?
//     startingSession.End - (End - currentTimestamp)
//     :
//     startingSession.Start;
// }

// function countClosedSessions(sessions, timeFrame) {
//   // because some markets close for lunch, count only overnight closed sessions
//   for (var i = 0, count = 0; i < sessions.length - 1, count < timeFrame; i++) {
//     const { End, Start, Kind } = sessions[i];
//     const notLunch = End - Start > 1000 * 60 * 60 * 3;
//     if (Kind === 'Closed' && notLunch && i > 0) {
//       count++;
//     }
//   }
//   return findFirstTradingSession(sessions.slice(i));
// }

// function findFirstTradingSession(sessions) {
//   const firstTradingSession = sessions.find(session => session.Kind === 'Trading');
//   return firstTradingSession.End;
// }

// function getQueryStart(startData) {
//   // start date for hstri query is earliest start date among tickers
//   const startTimes = Object.values(startData);
//   return Math.min(...startTimes);
// }

// createDataParser helper functions
function checkShowAsPercent(series, isPercentProp) {
  // "Show as percent change" toggle should be locked into true or false in certain situations
  const hasBonds = series.some(checkBonds);
  const differentUnitSymbols = series.some((currentSeries, i) =>
    checkUnitSymbols(series[0], currentSeries, i));
  const disabled = hasBonds || differentUnitSymbols;
  const isPercent = !hasBonds && (differentUnitSymbols || isPercentProp);
  return { isPercent, disabled };
}

function checkBonds(series, i) {
  const instrumentType = getSeriesProp(series, i, 'InstrumentType');
  return instrumentType === 'Bond';
}

function getSeriesProp(series, i, prop) {
  return series.raw.Series[i][prop];
}

function checkUnitSymbols(firstSeries, currentSeries, i) {
  return getUnitSymbol(currentSeries, i) !== getUnitSymbol(firstSeries, 0);
}

function getUnitSymbol(series, i) {
  return getSeriesProp(series, i, 'FormatHints').UnitSymbol;
}

function mustShowAsPercent(showAsPercentProps) {
  const { isPercent, disabled } = showAsPercentProps;
  return isPercent && disabled;
}

function buildDatesArr(res, rawDateArr, end, isCustom, frequency, needsLast) {
  const compiledDates = compileDates(res, rawDateArr);
  const lastDate = compiledDates[getLastIdx(compiledDates)];
  
  const longerThanDaily = checkLongerThanDaily(frequency);
  
  const isBefore = longerThanDaily && checkBefore(frequency, lastDate, end);
  
  return isCustom && end && !needsLast && isBefore ?
    compiledDates.slice(0, -1) : compiledDates;
}

function compileDates(res, rawDateArr) {
  res.forEach(series => rawDateArr.push(...series.ticks));
  const uniqueDates = new Set(rawDateArr);
  return [...uniqueDates].filter(date => date)
    .sort((a, b) => a - b);
}

function getLastIdx(arr) {
  return arr.length - 1;
}

function checkLongerThanDaily(frequency) {
  return ['P7D', 'P1M', 'P3M', 'P1Y'].some(code => code === frequency);
}

function checkBefore(frequency, lastDate, end) {
  const date = moment(lastDate);
  return convertDate(date, frequency).isAfter(end);
}

function convertDate(date, frequency) {
  switch (frequency) {
    case 'P7D':
      return date.add(5, 'd');
    case 'P1M':
      return date.utc().endOf('month');
    case 'P3M':
      return date.utc().add(2, 'M').endOf('month');
    case 'P1Y':
      return date.utc().endOf('year');
    default:
      return date;
  }
}

function findStartDates(series, dateRange) {
  return series.map(currentSeries => {
    const { MarketSessions, ExtraData, InstrumentType } = currentSeries;
    if (MarketSessions && InstrumentType !== 'Future') {
      const priorClose = findPriorClose(ExtraData).Date;
      const closingTime = MarketSessions.reverse().find(session => session.Kind === 'Trading').End;
      const date = moment(priorClose).utc();
      const year = date.year();
      const month = date.month();
      const day = date.date();
      const time = moment(closingTime);
      const hour = time.hour();
      const minutes = time.minute();
      const seconds = time.second();
      return +moment().year(year).month(month).date(day).hour(hour).minute(minutes).second(seconds).format('x');
    }
    if (dateRange === 'YTD') {
      return findPriorClose(ExtraData).Date;
    }
  });
}

// function findSessionStart(sessions) {
//   return sessions.find(session => session.Kind === 'Closed').Start;
// }

function findPriorClose(extraData) {
  return extraData.find(data => data.Name === 'PriorClose');
}

function createCategories(dates, needsLast, lastIndex, isIntraday, frequency) {
  return dates.map((x, i) => {
    const isLast = needsLast && i === lastIndex;
    return formatCategory(x, isIntraday, frequency, isLast);
  });
}

function formatCategory(x, isIntraday, frequency, isLast) {
  const date = getDate(x, isIntraday, frequency, isLast);
  const dayFormat = 'MM/DD/YYYY';
  return isIntraday ?
    date.format(`${dayFormat} h:mm:ss a`).slice(0, -1)
    :
    date.format(dayFormat);
}

function getDate(x, isIntraday, frequency, isLast) {
  const needsToday = checkNeedsToday(frequency, isLast);
  const date = getETDate(x, frequency, needsToday);
  return isIntraday || needsToday ?
    date : date.utc();
}

function checkNeedsToday(frequency, isLast) {
  return isLast && checkLongerThanDaily(frequency);
}

function getETDate(x, frequency, needsToday) {
  return needsToday ?
    moment.tz('America/New_York')
    :
    convertDate(createETMoment(x), frequency);
}

function formatAsOfDate(x, isIntraday, frequency) {
  const date = getDate(x, isIntraday, frequency, true);
  const month = MONTHS[date.month()];
  const day = date.date();
  const formattedDate = `${month} ${day}`;
  const time = date.format('h:mm a');
  const formattedTime = isIntraday ?
    `, ${time.slice(0, -1)}.m. ET`
    :
    '';
  return formattedDate + formattedTime;
}

function buildDataTemplate(dates) {
  const dataTemplate = {};
  dates.forEach(date => {
    dataTemplate[date] = null;
  });
  return dataTemplate;
}

function mapSeriesData(data, usePercent, tickerType, firstDate, firstValue, dataTemplate) {
  const seriesData = Object.assign({}, dataTemplate);

  const notPercent = !usePercent || tickerType === 'Bond';

  const value = notPercent ? firstValue : 0;
  if (firstDate) seriesData[firstDate] = value;

  const initialY = firstDate ? firstValue : findFirstY(data);

  data.forEach((item, index) => {
    const x = item[0];
    const y = getY(item[1], initialY, notPercent);    
    if (seriesData[x] === null) seriesData[x] = y;
  });

  return Object.values(seriesData).map(y => {
    return { y };
  })
}

function findFirstY(data) {
  // find first non-null Y value (needed to calculate percent change)
  return data.find(point => point[1])[1];
}

function getY(y, initialY, notPercent) {
  return notPercent || !y ?
    y : getChange(initialY, y);
}

function getChange(initialY, y) {
  // percentage change from first Y value
  return (y - initialY) / initialY * 100;
}

export {
  parseURL,
  getTickerSymbolIndex,
  createDataFormatter,
  fetchData,
  createDataParser
}


// WEBPACK FOOTER //
// ./server/shared/marketDataHelpers.js